import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Users, GraduationCap, Ticket, DollarSign, BookOpen, Trash2, Power, Plus, Loader2, Wand2, CheckCircle2, ExternalLink, RefreshCw, UploadCloud, Link2, Eye, EyeOff, Pencil, Save, X, FileText, ShieldCheck, ClipboardList } from "lucide-react";
import { Link } from "react-router-dom";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");
const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
const GRADES = ["K", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];
const GRADE_LABEL = (g) => (g === "K" ? "Kindergarten" : `Grade ${g}`);

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="border border-border bg-card p-6">
      <Icon className="h-6 w-6 text-primary" />
      <p className="mt-4 font-head text-3xl font-black text-foreground" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>{value}</p>
      <p className="label-caps mt-1 text-muted-foreground">{label}</p>
    </div>
  );
}

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [codes, setCodes] = useState([]);
  const [users, setUsers] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [txns, setTxns] = useState([]);
  const [form, setForm] = useState({ code: "", kind: "percent", value: "", applies_to: "all", max_uses: "" });
  const [busy, setBusy] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [gen, setGen] = useState({ subject_id: "", grade_level: "K", topic: "", num_units: 3, lessons_per_unit: 3, publish: true });
  const [genBusy, setGenBusy] = useState(false);
  const [genResult, setGenResult] = useState(null);
  const [genProgress, setGenProgress] = useState(null);
  const [courseCounts, setCourseCounts] = useState({});
  const [totalCourses, setTotalCourses] = useState(0);
  const [sync, setSync] = useState(null);
  const [syncBusy, setSyncBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selUsers, setSelUsers] = useState(() => new Set());
  const [selEnr, setSelEnr] = useState(() => new Set());
  const [coverEnr, setCoverEnr] = useState([]);
  const [enrollDlg, setEnrollDlg] = useState(null); // { user_id, name, grade_level, student_name }
  const [enrollBusy, setEnrollBusy] = useState(false);
  const [progress, setProgress] = useState([]);
  const [premiumCourses, setPremiumCourses] = useState([]);
  const [premiumSearch, setPremiumSearch] = useState("");
  const [priceEdits, setPriceEdits] = useState({});
  const [premiumSubjects, setPremiumSubjects] = useState([]);
  const [bundleEdits, setBundleEdits] = useState({}); // subject_id -> price input
  const [backupBusy, setBackupBusy] = useState(false);

  const downloadBackup = async () => {
    setBackupBusy(true);
    try {
      const res = await api.get("/admin/db/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const disp = res.headers?.["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/.exec(disp);
      const a = document.createElement("a");
      a.href = url;
      a.download = match ? match[1] : `hornsby-backup-${Date.now()}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded");
    } catch (e) { toast.error("Backup failed: " + (e.response?.data?.detail || e.message)); }
    finally { setBackupBusy(false); }
  };

  const loadPremium = async () => {
    try { const { data } = await api.get("/admin/premium-courses"); setPremiumCourses(data); } catch {}
    try { const { data } = await api.get("/admin/premium-subjects"); setPremiumSubjects(data); } catch {}
  };
  const toggleBundle = async (s) => {
    const next = !s.is_premium_bundle;
    try {
      await api.patch(`/admin/subjects/${s.subject_id}/bundle`, { is_premium_bundle: next });
      toast.success(next ? `"${s.name}" is now a program bundle` : `"${s.name}" bundle disabled`);
      await loadPremium();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveBundlePrice = async (s) => {
    const val = parseFloat(bundleEdits[s.subject_id]);
    if (isNaN(val) || val < 0) { toast.error("Enter a valid price"); return; }
    try {
      await api.patch(`/admin/subjects/${s.subject_id}/bundle`, { bundle_price: val });
      toast.success(`${s.name} program price set to $${val.toFixed(2)}`);
      setBundleEdits((p) => { const n = { ...p }; delete n[s.subject_id]; return n; });
      await loadPremium();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const togglePremium = async (c) => {
    const next = !c.is_premium;
    try {
      await api.patch(`/admin/courses/${c.course_id}/premium`, { is_premium: next, ...(next && !c.premium_price ? { premium_price: 49 } : {}) });
      toast.success(next ? `"${c.title}" is now premium` : `"${c.title}" is no longer premium`);
      await loadPremium(); await loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const savePrice = async (c) => {
    const val = parseFloat(priceEdits[c.course_id]);
    if (isNaN(val) || val < 0) { toast.error("Enter a valid price"); return; }
    try { await api.patch(`/admin/courses/${c.course_id}/premium`, { premium_price: val }); toast.success(`Price set to $${val.toFixed(2)}`); setPriceEdits((p) => { const n = { ...p }; delete n[c.course_id]; return n; }); await loadPremium(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const loadProgress = async () => {
    try { const { data } = await api.get("/admin/progress"); setProgress(data); } catch {}
  };
  const grantEnroll = async () => {
    setEnrollBusy(true);
    try {
      await api.post("/admin/enrollments", { user_id: enrollDlg.user_id, grade_level: enrollDlg.grade_level, student_name: enrollDlg.student_name || null });
      toast.success(`Enrolled ${enrollDlg.name} in ${GRADE_LABEL(enrollDlg.grade_level)}`);
      setEnrollDlg(null); await loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setEnrollBusy(false); }
  };
  const grantUnlock = async () => {
    if (!enrollDlg.unlock_course_id) { toast.error("Pick a premium course"); return; }
    setEnrollBusy(true);
    try {
      const { data } = await api.post("/admin/course-unlocks", { user_id: enrollDlg.user_id, course_id: enrollDlg.unlock_course_id });
      toast.success(data.already ? "Already unlocked for this user" : `Premium course unlocked for ${enrollDlg.name}`);
      await loadPremium();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setEnrollBusy(false); }
  };

  const loadCover = async () => {
    try { const { data } = await api.get("/admin/cover-school"); setCoverEnr(data); } catch {}
  };

  const toggleSet = (set, setter, id) => {
    const n = new Set(set); n.has(id) ? n.delete(id) : n.add(id); setter(n);
  };

  const delUser = async (id) => {
    if (!window.confirm("Permanently delete this user and ALL of their data (enrollments, payments, progress)? This cannot be undone.")) return;
    try { await api.delete(`/admin/users/${id}`); toast.success("User permanently deleted"); setSelUsers(new Set()); await loadAll(); await loadCover(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const bulkDelUsers = async () => {
    if (!selUsers.size) return;
    if (!window.confirm(`Permanently delete ${selUsers.size} user(s) and ALL of their data? This cannot be undone.`)) return;
    try { const { data } = await api.post("/admin/users/bulk-delete", { ids: [...selUsers] }); toast.success(`Deleted ${data.deleted} user(s)`); setSelUsers(new Set()); await loadAll(); await loadCover(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delEnrollment = async (id) => {
    if (!window.confirm("Permanently delete this enrollment? This cannot be undone.")) return;
    try { await api.delete(`/admin/enrollments/${id}`); toast.success("Enrollment deleted"); setSelEnr(new Set()); await loadAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const bulkDelEnr = async () => {
    if (!selEnr.size) return;
    if (!window.confirm(`Permanently delete ${selEnr.size} enrollment(s)? This cannot be undone.`)) return;
    try { const { data } = await api.post("/admin/enrollments/bulk-delete", { ids: [...selEnr] }); toast.success(`Deleted ${data.deleted} enrollment(s)`); setSelEnr(new Set()); await loadAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openCoverDoc = async (enrollmentId, type) => {
    try {
      const { data } = await api.get(`/admin/cover-school/${enrollmentId}/document/${type}`);
      const w = window.open("", "_blank");
      w.document.write(`<html><head><title>${data.title}</title></head><body style="font-family:Georgia,serif;max-width:820px;margin:40px auto;padding:0 24px;color:#1a1a1a;line-height:1.6">${data.html}<script>window.onload=()=>setTimeout(()=>window.print(),400)</script></body></html>`);
      w.document.close();
    } catch (e) { toast.error("Failed to load document"); }
  };

  const loadSync = async () => {
    try { const { data } = await api.get("/admin/sync/status"); setSync(data); return data; }
    catch { return null; }
  };

  const pollSync = () => {
    let tries = 0;
    const t = setInterval(async () => {
      tries += 1;
      const d = await loadSync();
      const st = d?.state?.status;
      const settled = st && !["running", "applying"].includes(st);
      if (settled || tries > 80) {
        clearInterval(t); setSyncBusy(false);
        if (st === "staged") { api.get("/admin/sync/preview").then((r) => setPreview(r.data)).catch(() => {}); toast.success("Curriculum staged — review then apply."); }
        if (st === "applied") { toast.success(`Applied ${d.state?.applied_counts?.lessons || 0} lessons live.`); loadAll(); }
        if (st === "error") toast.error("Sync failed: " + (d.state?.error || "unknown"));
        if (st === "apply_error") toast.error("Apply failed: " + (d.state?.apply_error || "unknown"));
        if (tries > 80 && (st === "running" || st === "applying")) toast.error("Sync is taking too long — check status shortly.");
      }
    }, 3000);
  };

  const startSync = async (incremental, force = false) => {
    setSyncBusy(true); setPreview(null);
    try { await api.post("/admin/sync/pull", { incremental, force }); toast.info("Sync started — pulling curriculum…"); pollSync(); }
    catch (e) { setSyncBusy(false); await loadSync(); toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const applySync = async () => {
    setSyncBusy(true);
    try { await api.post("/admin/sync/apply"); toast.info("Applying staged curriculum to live…"); pollSync(); }
    catch (e) { setSyncBusy(false); toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Subjects manager
  const [adminSubjects, setAdminSubjects] = useState([]);
  const [subjForm, setSubjForm] = useState({ name: "", color: "#a84a27", description: "" });
  const [subjBusy, setSubjBusy] = useState(false);
  const [editSubj, setEditSubj] = useState(null); // {subject_id, name, color}

  const loadAdminSubjects = async () => {
    try { const { data } = await api.get("/admin/subjects"); setAdminSubjects(data); } catch {}
  };
  const createSubject = async (e) => {
    e.preventDefault(); setSubjBusy(true);
    try { await api.post("/admin/subjects", subjForm); toast.success("Subject added"); setSubjForm({ name: "", color: "#a84a27", description: "" }); await loadAdminSubjects(); await loadAll(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSubjBusy(false); }
  };
  const toggleHide = async (s) => { await api.patch(`/admin/subjects/${s.subject_id}`, { hidden: !s.hidden }); await loadAdminSubjects(); };
  const setSubjectCategory = async (s, category) => {
    try { await api.patch(`/admin/subjects/${s.subject_id}`, { category }); toast.success("Group updated"); await loadAdminSubjects(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveEdit = async () => {
    try { await api.patch(`/admin/subjects/${editSubj.subject_id}`, { name: editSubj.name, color: editSubj.color }); toast.success("Updated"); setEditSubj(null); await loadAdminSubjects(); await loadAll(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const delSubject = async (s) => {
    try { await api.delete(`/admin/subjects/${s.subject_id}`); toast.success("Deleted"); await loadAdminSubjects(); await loadAll(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  // Extra Practice generator
  const [allCourses, setAllCourses] = useState([]);
  const [ep, setEp] = useState({ course_id: "", lesson_id: "", practice: true, review: false, worksheet: false, num_questions: 8, difficulty: "medium" });
  const [epLessons, setEpLessons] = useState([]);
  const [epBusy, setEpBusy] = useState(false);
  const [epResult, setEpResult] = useState(null);

  const pickCourse = async (cid) => {
    setEp((p) => ({ ...p, course_id: cid, lesson_id: "" })); setEpLessons([]); setEpResult(null);
    try {
      const { data } = await api.get(`/courses/${cid}`);
      const lessons = (data.units || []).flatMap((u) => (u.lessons || []).map((l) => ({ ...l, unit: u.title })));
      setEpLessons(lessons);
      if (lessons[0]) setEp((p) => ({ ...p, lesson_id: lessons[0].lesson_id }));
    } catch {}
  };
  const generatePractice = async (e) => {
    e.preventDefault();
    const kinds = ["practice", "review", "worksheet"].filter((k) => ep[k]);
    if (!ep.lesson_id) { toast.error("Pick a lesson"); return; }
    if (kinds.length === 0) { toast.error("Select at least one type"); return; }
    setEpBusy(true); setEpResult(null);
    setGenProgress({ message: "Starting up…", kinds_done: 0, kinds_total: kinds.length });
    try {
      const { data } = await api.post("/admin/generate-practice", { lesson_id: ep.lesson_id, kinds, num_questions: Number(ep.num_questions), difficulty: ep.difficulty });
      const jobId = data.job_id;
      let tries = 0;
      const poll = async () => {
        tries += 1;
        try {
          const { data: job } = await api.get(`/admin/generate-practice/status/${jobId}`);
          if (job.progress) setGenProgress(job.progress);
          if (job.status === "done") {
            setEpResult({ created: job.created || [], lesson_id: job.lesson_id, course_id: job.course_id });
            setGenProgress(null); setEpBusy(false);
            toast.success(`Created ${(job.created || []).length} set(s)`);
            return;
          }
          if (job.status === "error") {
            setGenProgress(null); setEpBusy(false);
            toast.error(job.error || "Generation failed.");
            return;
          }
        } catch { /* transient — keep polling */ }
        if (tries > 120) { setGenProgress(null); setEpBusy(false); toast.error("Taking longer than expected — check the lesson shortly."); return; }
        setTimeout(poll, 2000);
      };
      setTimeout(poll, 2000);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setGenProgress(null); setEpBusy(false); }
  };

  const loadAll = async () => {
    const [s, c, u, e, t, subj, courses] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/discount-codes"),
      api.get("/admin/users"), api.get("/admin/enrollments"), api.get("/admin/transactions"),
      api.get("/subjects"), api.get("/courses"),
    ]);
    setStats(s.data); setCodes(c.data); setUsers(u.data); setEnrollments(e.data); setTxns(t.data);
    setSubjects(subj.data);
    const counts = {};
    courses.data.forEach((co) => { counts[co.subject_id] = (counts[co.subject_id] || 0) + 1; });
    setCourseCounts(counts);
    setTotalCourses(courses.data.length);
    setAllCourses(courses.data);
    if (subj.data.length && !gen.subject_id) setGen((g) => ({ ...g, subject_id: subj.data[0].subject_id }));
  };

  const generateCourse = async (e) => {
    e.preventDefault();
    setGenBusy(true);
    setGenResult(null);
    setGenProgress({ message: "Starting up…", units_done: 0, units_total: Number(gen.num_units), lessons_done: 0, lessons_total: Number(gen.num_units) * Number(gen.lessons_per_unit) });
    try {
      const { data } = await api.post("/admin/generate-course", {
        subject_id: gen.subject_id, grade_level: gen.grade_level, topic: gen.topic || null,
        num_units: Number(gen.num_units), lessons_per_unit: Number(gen.lessons_per_unit), publish: gen.publish,
      });
      // Poll the background job until done/error (up to ~6 minutes)
      const jobId = data.job_id;
      let tries = 0;
      const poll = async () => {
        tries += 1;
        try {
          const { data: job } = await api.get(`/admin/generate-course/status/${jobId}`);
          if (job.progress) setGenProgress(job.progress);
          if (job.status === "done") {
            const p = job.progress || {};
            setGenResult({ course: job.course, units: p.units_done, lessons: p.lessons_done });
            setGenProgress(null);
            setGenBusy(false);
            toast.success(`Created "${job.course?.title || "course"}"`);
            loadAll();
            return;
          }
          if (job.status === "error") {
            setGenProgress(null);
            setGenBusy(false);
            toast.error(job.error || "Course generation failed.");
            return;
          }
        } catch {
          // transient error — keep polling
        }
        if (tries > 180) {
          setGenProgress(null);
          setGenBusy(false);
          toast.error("Generation is taking longer than expected. Check the catalog shortly.");
          loadAll();
          return;
        }
        setTimeout(poll, 2000);
      };
      setTimeout(poll, 2000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setGenProgress(null);
      setGenBusy(false);
    }
  };

  useEffect(() => { loadAll().catch(() => toast.error("Failed to load admin data")); loadSync(); loadAdminSubjects(); loadCover(); loadProgress(); loadPremium(); }, []);

  const createCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/discount-codes", {
        code: form.code, kind: form.kind, value: parseFloat(form.value),
        applies_to: form.applies_to, active: true,
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
      });
      toast.success("Discount code created");
      setForm({ code: "", kind: "percent", value: "", applies_to: "all", max_uses: "" });
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const toggleCode = async (code) => { await api.patch(`/admin/discount-codes/${code}`); await loadAll(); };
  const delCode = async (code) => { await api.delete(`/admin/discount-codes/${code}`); toast.success("Deleted"); await loadAll(); };

  if (!stats) return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="mx-auto max-w-7xl px-5 py-12 lg:px-8">
      <div className="border-b border-border pb-6">
        <span className="label-caps text-primary">Command Center</span>
        <h1 className="mt-2 font-head text-4xl font-black">Admin Dashboard</h1>
      </div>

      <div className="mt-8 grid grid-cols-2 gap-px border border-border bg-border md:grid-cols-3 lg:grid-cols-6">
        <Stat icon={Users} label="Users" value={stats.users} />
        <Stat icon={GraduationCap} label="Enrollments" value={stats.enrollments} />
        <Stat icon={DollarSign} label="Memberships" value={stats.memberships} />
        <Stat icon={Ticket} label="Codes" value={stats.discount_codes} />
        <Stat icon={BookOpen} label="Courses" value={stats.courses} />
        <Stat icon={DollarSign} label="Revenue" value={`$${stats.revenue}`} />
      </div>

      <Tabs defaultValue="curriculum" className="mt-12">
        <TabsList className="flex flex-wrap justify-start rounded-none border border-border bg-card p-0">
          <TabsTrigger value="curriculum" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-curriculum">Curriculum</TabsTrigger>
          <TabsTrigger value="subjects" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-subjects">Subjects</TabsTrigger>
          <TabsTrigger value="practice" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-practice">Extra Practice</TabsTrigger>
          <TabsTrigger value="codes" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-codes">Discount Codes</TabsTrigger>
          <TabsTrigger value="premium" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-premium">Premium</TabsTrigger>
          <TabsTrigger value="generate" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-generate">AI Generate</TabsTrigger>
          <TabsTrigger value="sync" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-sync">Sync</TabsTrigger>
          <TabsTrigger value="users" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-users">Users</TabsTrigger>
          <TabsTrigger value="enrollments" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-enrollments">Enrollments</TabsTrigger>
          <TabsTrigger value="gradebook" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-gradebook">Gradebook</TabsTrigger>
          <TabsTrigger value="cover-school" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-cover-school">Cover School</TabsTrigger>
          <TabsTrigger value="txns" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="tab-txns">Transactions</TabsTrigger>
        </TabsList>

        {/* Curriculum */}
        <TabsContent value="curriculum" className="mt-6">
          <div className="flex flex-wrap items-end justify-between gap-4 border-b border-border pb-5">
            <div>
              <h2 className="font-head text-2xl font-bold">Curriculum Library</h2>
              <p className="mt-1 text-sm text-muted-foreground">{totalCourses} active courses across {subjects.length} subjects · K–12. Click a subject to browse & test in preview.</p>
            </div>
            <Link to="/curriculum/browse" data-testid="admin-open-preview">
              <Button className="rounded-none label-caps bg-primary hover:bg-primary/80"><BookOpen className="mr-2 h-4 w-4" /> Open All-Grades Preview</Button>
            </Link>
          </div>
          {[
            { key: "core", label: "Core Subjects", color: "#3B82F6" },
            { key: "life_skills", label: "Real-World Life Skills", color: "#CA8A04" },
            { key: "college_prep", label: "College Prep", color: "#7C3AED" },
          ].map((grp) => {
            const list = subjects
              .filter((s) => (s.category || "core") === grp.key)
              .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
            if (!list.length) return null;
            return (
              <div key={grp.key} className="mt-8" data-testid={`admin-curr-group-${grp.key}`}>
                <div className="mb-4 flex items-center gap-3 border-b-2 border-border pb-2">
                  <span className="h-5 w-1.5 rounded-full" style={{ background: grp.color }} />
                  <h3 className="font-head text-xl font-black" style={{ color: grp.color }}>{grp.label}</h3>
                  <span className="label-caps text-muted-foreground">{list.length}</span>
                </div>
                <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {list.map((s) => {
                    const count = courseCounts[s.subject_id] || 0;
                    return (
                      <Link
                        key={s.subject_id}
                        to={`/curriculum/browse?subject=${s.subject_id}`}
                        data-testid={`admin-subject-${s.subject_id}`}
                        className="group flex flex-col border border-border bg-card p-6 transition-all hover:-translate-y-1 hover:border-primary hover:shadow-brutal"
                        style={{ borderTopWidth: 3, borderTopColor: s.color }}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="font-head text-xl font-bold group-hover:text-primary">{s.name}</h3>
                          {count === 0 && <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[0.55rem] font-black uppercase tracking-wide text-amber-700">Coming Soon</span>}
                        </div>
                        {s.description && <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{s.description}</p>}
                        <span className="mt-4 label-caps text-muted-foreground">{count} course{count === 1 ? "" : "s"} →</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </TabsContent>

        {/* Subjects manager */}
        <TabsContent value="subjects" className="mt-6">
          <form onSubmit={createSubject} className="grid gap-4 border border-border bg-card p-6 sm:grid-cols-4" data-testid="subject-form">
            <div className="sm:col-span-2">
              <Label className="label-caps text-muted-foreground">Subject Name</Label>
              <Input value={subjForm.name} onChange={(e) => setSubjForm({ ...subjForm, name: e.target.value })} required placeholder="e.g. Woodworking" data-testid="subject-name" className="mt-2 rounded-none border-border bg-background" />
            </div>
            <div>
              <Label className="label-caps text-muted-foreground">Color</Label>
              <input type="color" value={subjForm.color} onChange={(e) => setSubjForm({ ...subjForm, color: e.target.value })} data-testid="subject-color" className="mt-2 h-10 w-full cursor-pointer border border-border bg-background" />
            </div>
            <div className="flex items-end">
              <Button type="submit" disabled={subjBusy} className="w-full rounded-none label-caps bg-primary hover:bg-primary/80" data-testid="subject-submit">{subjBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1 h-4 w-4" /> Add</>}</Button>
            </div>
            <div className="sm:col-span-4">
              <Label className="label-caps text-muted-foreground">Description (optional)</Label>
              <Input value={subjForm.description} onChange={(e) => setSubjForm({ ...subjForm, description: e.target.value })} className="mt-2 rounded-none border-border bg-background" />
            </div>
          </form>

          <div className="mt-6 overflow-x-auto border border-border">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-border bg-card"><tr className="label-caps text-muted-foreground"><th className="p-4">Subject</th><th className="p-4">Courses</th><th className="p-4">Group</th><th className="p-4">Visibility</th><th className="p-4">Actions</th></tr></thead>
              <tbody>
                {adminSubjects.map((s) => (
                  <tr key={s.subject_id} className="border-b border-border last:border-0" data-testid={`subject-row-${s.subject_id}`}>
                    <td className="p-4">
                      {editSubj?.subject_id === s.subject_id ? (
                        <div className="flex items-center gap-2">
                          <input type="color" value={editSubj.color} onChange={(e) => setEditSubj({ ...editSubj, color: e.target.value })} className="h-8 w-10 cursor-pointer border border-border" />
                          <Input value={editSubj.name} onChange={(e) => setEditSubj({ ...editSubj, name: e.target.value })} className="h-8 rounded-none border-border bg-background" />
                        </div>
                      ) : (
                        <div className="flex items-center gap-3">
                          <span className="h-4 w-4 shrink-0 border border-border" style={{ background: s.color }} />
                          <span className="font-semibold">{s.name}</span>
                        </div>
                      )}
                    </td>
                    <td className="p-4">{s.course_count}</td>
                    <td className="p-4">
                      <Select value={s.category || "core"} onValueChange={(v) => setSubjectCategory(s, v)}>
                        <SelectTrigger className="h-9 w-[190px] rounded-none" data-testid={`subject-group-select-${s.subject_id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="core">Core Subjects</SelectItem>
                          <SelectItem value="life_skills">Real-World Life Skills</SelectItem>
                          <SelectItem value="college_prep">College Prep</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-4"><span className={`label-caps ${s.hidden ? "text-muted-foreground" : "text-primary"}`}>{s.hidden ? "Hidden" : "Visible"}</span></td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        {editSubj?.subject_id === s.subject_id ? (
                          <>
                            <button onClick={saveEdit} className="text-primary hover:opacity-70" data-testid={`subject-save-${s.subject_id}`}><Save className="h-4 w-4" /></button>
                            <button onClick={() => setEditSubj(null)} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => setEditSubj({ subject_id: s.subject_id, name: s.name, color: s.color || "#a84a27" })} className="text-muted-foreground hover:text-primary" data-testid={`subject-edit-${s.subject_id}`}><Pencil className="h-4 w-4" /></button>
                            <button onClick={() => toggleHide(s)} className="text-muted-foreground hover:text-primary" data-testid={`subject-hide-${s.subject_id}`}>{s.hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                            <button onClick={() => delSubject(s)} className="text-muted-foreground hover:text-destructive" data-testid={`subject-delete-${s.subject_id}`}><Trash2 className="h-4 w-4" /></button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">Subjects with courses can't be deleted — hide them instead. Renaming/recoloring updates all their courses.</p>
        </TabsContent>

        {/* Extra Practice generator */}
        <TabsContent value="practice" className="mt-6">
          <div className="grid gap-6 lg:grid-cols-2">
            <form onSubmit={generatePractice} className="border border-border bg-card p-6" data-testid="practice-form">
              <div className="flex items-center gap-2 text-primary"><Wand2 className="h-5 w-5" /><span className="label-caps">AI Extra Schoolwork</span></div>
              <h3 className="mt-2 font-head text-2xl font-bold">Generate Extra Practice</h3>
              <p className="mt-1 text-sm text-muted-foreground">Adds auto-graded practice straight onto a lesson. Uses your OpenAI key.</p>

              <div className="mt-6 space-y-4">
                <div>
                  <Label className="label-caps text-muted-foreground">Course</Label>
                  <Select value={ep.course_id} onValueChange={pickCourse}>
                    <SelectTrigger className="mt-2 rounded-none border-border bg-background" data-testid="practice-course"><SelectValue placeholder="Choose a course" /></SelectTrigger>
                    <SelectContent className="rounded-none max-h-72">
                      {allCourses.map((c) => <SelectItem key={c.course_id} value={c.course_id}>{c.grade_level === "K" ? "K" : "G" + c.grade_level} · {c.title}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-caps text-muted-foreground">Lesson</Label>
                  <Select value={ep.lesson_id} onValueChange={(v) => setEp({ ...ep, lesson_id: v })} disabled={!epLessons.length}>
                    <SelectTrigger className="mt-2 rounded-none border-border bg-background" data-testid="practice-lesson"><SelectValue placeholder={epLessons.length ? "Choose a lesson" : "Pick a course first"} /></SelectTrigger>
                    <SelectContent className="rounded-none max-h-72">
                      {epLessons.map((l) => <SelectItem key={l.lesson_id} value={l.lesson_id}>{l.title}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="label-caps text-muted-foreground">What to Create</Label>
                  <div className="mt-2 space-y-2">
                    {[["practice", "Extra Practice Set"], ["review", "Unit Review Test"], ["worksheet", "Printable Worksheet"]].map(([k, lbl]) => (
                      <button type="button" key={k} onClick={() => setEp({ ...ep, [k]: !ep[k] })} data-testid={`practice-kind-${k}`}
                        className={`flex w-full items-center gap-3 border p-3 text-left text-sm transition-colors ${ep[k] ? "border-primary bg-primary/10" : "border-border hover:border-primary/60"}`}>
                        {ep[k] ? <CheckCircle2 className="h-5 w-5 text-primary" /> : <span className="h-5 w-5 border border-muted-foreground" />}
                        {lbl}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="label-caps text-muted-foreground">Questions (3-15)</Label>
                    <Input type="number" min={3} max={15} value={ep.num_questions} onChange={(e) => setEp({ ...ep, num_questions: e.target.value })} data-testid="practice-count" className="mt-2 rounded-none border-border bg-background" />
                  </div>
                  <div>
                    <Label className="label-caps text-muted-foreground">Difficulty</Label>
                    <Select value={ep.difficulty} onValueChange={(v) => setEp({ ...ep, difficulty: v })}>
                      <SelectTrigger className="mt-2 rounded-none border-border bg-background" data-testid="practice-difficulty"><SelectValue /></SelectTrigger>
                      <SelectContent className="rounded-none"><SelectItem value="easy">Easy</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="hard">Hard</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <Button type="submit" disabled={epBusy || !ep.lesson_id} className="mt-6 w-full rounded-none label-caps bg-primary py-6 hover:bg-primary/80" data-testid="practice-submit">
                {epBusy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating in background…</> : <><Wand2 className="mr-2 h-4 w-4" /> Generate Practice</>}
              </Button>
              <p className="mt-2 text-center text-xs text-muted-foreground">Adds a new auto-graded quiz to the lesson's <strong>Quiz</strong> tab. It does not change your existing lesson content.</p>
            </form>

            <div className="border border-border bg-card p-6">
              <span className="label-caps text-muted-foreground">Result</span>
              {epBusy ? (
                <div className="py-12 text-center" data-testid="practice-progress">
                  <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
                  <p className="mt-4 text-sm font-semibold text-foreground">{genProgress?.message || "Writing questions…"}</p>
                  {genProgress?.kinds_total > 0 && (
                    <p className="mt-1 text-xs text-muted-foreground">{genProgress.kinds_done || 0}/{genProgress.kinds_total} set(s) done</p>
                  )}
                </div>
              ) : epResult ? (
                <div className="mt-4" data-testid="practice-result">
                  <div className="flex items-center gap-2 text-primary"><CheckCircle2 className="h-6 w-6" /><span className="font-head text-xl font-bold text-foreground">Added to the Lesson</span></div>
                  <ul className="mt-4 space-y-2">
                    {epResult.created.map((c) => (
                      <li key={c.assessment_id} className="border border-border bg-background p-3 text-sm">
                        <p className="font-semibold">{c.title}</p>
                        <p className="text-xs text-muted-foreground capitalize">{c.kind} · {c.question_count} questions · {c.total_points} pts</p>
                      </li>
                    ))}
                  </ul>
                  <Link to={`/lesson/${ep.lesson_id}`}><Button variant="outline" className="mt-5 rounded-none label-caps" data-testid="practice-view"><ExternalLink className="mr-2 h-4 w-4" /> Open Lesson</Button></Link>
                </div>
              ) : (
                <p className="mt-6 text-sm text-muted-foreground">Choose a course and lesson, tick what you want, and generate. New practice appears in that lesson's Quizzes — auto-graded, and worksheets are printable.</p>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Codes */}
        <TabsContent value="codes" className="mt-6">
          <form onSubmit={createCode} className="grid gap-4 border border-border bg-card p-6 md:grid-cols-5" data-testid="code-form">
            <div>
              <Label className="label-caps text-muted-foreground">Code</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} required placeholder="SPRING25" data-testid="code-input" className="mt-2 rounded-none border-border bg-background" />
            </div>
            <div>
              <Label className="label-caps text-muted-foreground">Type</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger className="mt-2 rounded-none border-border bg-background" data-testid="code-kind"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none"><SelectItem value="percent">Percent %</SelectItem><SelectItem value="fixed">Fixed $</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-caps text-muted-foreground">Value</Label>
              <Input type="number" step="0.01" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} required data-testid="code-value" className="mt-2 rounded-none border-border bg-background" />
            </div>
            <div>
              <Label className="label-caps text-muted-foreground">Applies To</Label>
              <Select value={form.applies_to} onValueChange={(v) => setForm({ ...form, applies_to: v })}>
                <SelectTrigger className="mt-2 rounded-none border-border bg-background" data-testid="code-applies"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none"><SelectItem value="all">All Plans</SelectItem><SelectItem value="annual">Annual</SelectItem><SelectItem value="monthly">Monthly</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button type="submit" disabled={busy} className="w-full rounded-none label-caps bg-primary hover:bg-primary/80" data-testid="code-submit">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1 h-4 w-4" /> Create</>}
              </Button>
            </div>
          </form>

          <div className="mt-6 overflow-x-auto border border-border">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-border bg-card"><tr className="label-caps text-muted-foreground">
                <th className="p-4">Code</th><th className="p-4">Discount</th><th className="p-4">Plan</th><th className="p-4">Uses</th><th className="p-4">Status</th><th className="p-4">Actions</th>
              </tr></thead>
              <tbody>
                {codes.map((c) => (
                  <tr key={c.code} className="border-b border-border last:border-0" data-testid={`code-row-${c.code}`}>
                    <td className="p-4 font-mono font-semibold text-primary">{c.code}</td>
                    <td className="p-4">{c.kind === "percent" ? `${c.value}%` : `$${c.value}`}</td>
                    <td className="p-4 capitalize">{c.applies_to}</td>
                    <td className="p-4">{c.uses || 0}{c.max_uses ? ` / ${c.max_uses}` : ""}</td>
                    <td className="p-4"><span className={`label-caps ${c.active ? "text-primary" : "text-muted-foreground"}`}>{c.active ? "Active" : "Off"}</span></td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        <button onClick={() => toggleCode(c.code)} className="text-muted-foreground hover:text-primary" data-testid={`code-toggle-${c.code}`}><Power className="h-4 w-4" /></button>
                        <button onClick={() => delCode(c.code)} className="text-muted-foreground hover:text-destructive" data-testid={`code-delete-${c.code}`}><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {codes.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No codes yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* AI Generate */}
        <TabsContent value="generate" className="mt-6">
          <div className="grid gap-6 lg:grid-cols-2">
            <form onSubmit={generateCourse} className="border border-border bg-card p-6" data-testid="generate-form">
              <div className="flex items-center gap-2 text-primary"><Wand2 className="h-5 w-5" /><span className="label-caps">AI Course Generation</span></div>
              <h3 className="mt-2 font-head text-2xl font-bold">Build a Course with GPT</h3>
              <p className="mt-1 text-sm text-muted-foreground">Generates a full course with units and lesson content. Uses your OpenAI key.</p>

              <div className="mt-6 space-y-4">
                <div>
                  <Label className="label-caps text-muted-foreground">Subject</Label>
                  <Select value={gen.subject_id} onValueChange={(v) => setGen({ ...gen, subject_id: v })}>
                    <SelectTrigger className="mt-2 rounded-none border-border bg-background" data-testid="gen-subject"><SelectValue placeholder="Choose subject" /></SelectTrigger>
                    <SelectContent className="rounded-none max-h-64">
                      {subjects.map((s) => <SelectItem key={s.subject_id} value={s.subject_id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="label-caps text-muted-foreground">Grade</Label>
                    <Select value={gen.grade_level} onValueChange={(v) => setGen({ ...gen, grade_level: v })}>
                      <SelectTrigger className="mt-2 rounded-none border-border bg-background" data-testid="gen-grade"><SelectValue /></SelectTrigger>
                      <SelectContent className="rounded-none max-h-64">
                        {GRADES.map((g) => <SelectItem key={g} value={g}>{GRADE_LABEL(g)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="label-caps text-muted-foreground">Focus / Topic (optional)</Label>
                    <Input value={gen.topic} onChange={(e) => setGen({ ...gen, topic: e.target.value })} placeholder="e.g. Fractions" data-testid="gen-topic" className="mt-2 rounded-none border-border bg-background" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="label-caps text-muted-foreground">Units (1-6)</Label>
                    <Input type="number" min={1} max={6} value={gen.num_units} onChange={(e) => setGen({ ...gen, num_units: e.target.value })} data-testid="gen-units" className="mt-2 rounded-none border-border bg-background" />
                  </div>
                  <div>
                    <Label className="label-caps text-muted-foreground">Lessons / Unit (1-6)</Label>
                    <Input type="number" min={1} max={6} value={gen.lessons_per_unit} onChange={(e) => setGen({ ...gen, lessons_per_unit: e.target.value })} data-testid="gen-lessons" className="mt-2 rounded-none border-border bg-background" />
                  </div>
                </div>
              </div>

              <Button type="submit" disabled={genBusy || !gen.subject_id} className="mt-6 w-full rounded-none label-caps bg-primary py-6 hover:bg-primary/80" data-testid="gen-submit">
                {genBusy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating in background…</> : <><Wand2 className="mr-2 h-4 w-4" /> Generate Course</>}
              </Button>
              <p className="mt-2 text-center text-xs text-muted-foreground">Runs in the background — you can keep working. Larger courses (up to 36 lessons) take a few minutes.</p>
            </form>

            <div className="border border-border bg-card p-6">
              <span className="label-caps text-muted-foreground">Result</span>
              {genBusy ? (
                <div className="py-10 text-center" data-testid="gen-progress">
                  <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
                  <p className="mt-4 text-sm font-semibold text-foreground">{genProgress?.message || "Working…"}</p>
                  {genProgress?.lessons_total > 0 && (
                    <>
                      <div className="mx-auto mt-4 h-3 w-full max-w-xs overflow-hidden rounded-full bg-border">
                        <div className="h-full bg-primary transition-all duration-500" style={{ width: `${Math.round(((genProgress.lessons_done || 0) / genProgress.lessons_total) * 100)}%` }} />
                      </div>
                      <p className="mt-2 text-xs text-muted-foreground">
                        Unit {genProgress.units_done || 0}/{genProgress.units_total} · {genProgress.lessons_done || 0}/{genProgress.lessons_total} lessons
                      </p>
                    </>
                  )}
                </div>
              ) : genResult ? (
                <div className="mt-4" data-testid="gen-result">
                  <div className="flex items-center gap-2 text-primary"><CheckCircle2 className="h-6 w-6" /><span className="font-head text-xl font-bold text-foreground">Course Created</span></div>
                  <p className="mt-3 font-head text-lg font-bold">{genResult.course.title}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{genResult.course.description}</p>
                  <div className="mt-4 flex gap-6 text-sm">
                    <span className="text-foreground">{genResult.units} units</span>
                    <span className="text-foreground">{genResult.lessons} lessons</span>
                  </div>
                  <Link to={`/course/${genResult.course.course_id}`}>
                    <Button variant="outline" className="mt-5 rounded-none label-caps" data-testid="gen-view-course"><ExternalLink className="mr-2 h-4 w-4" /> View Course</Button>
                  </Link>
                </div>
              ) : (
                <p className="mt-6 text-sm text-muted-foreground">Fill in the form and generate a course. It will be added to the catalog and appear under the chosen grade & subject.</p>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Sync */}
        <TabsContent value="sync" className="mt-6">
          <div className="border border-border bg-card p-6">
            <div className="flex items-center gap-2 text-primary"><Link2 className="h-5 w-5" /><span className="label-caps">Sister-Site Sync</span></div>
            <h3 className="mt-2 font-head text-2xl font-bold">Pull Curriculum from alcoverschool.com</h3>
            <p className="mt-1 text-sm text-muted-foreground">Source: <span className="font-mono text-foreground">{sync?.source || "—"}</span> · {sync?.configured ? "Key configured" : "Not configured"}. Pull downloads into a staging area you can preview, then Apply to go live.</p>

            <div className="mt-5 flex flex-wrap gap-3">
              <Button onClick={() => startSync(false)} disabled={syncBusy} className="rounded-none label-caps bg-primary hover:bg-primary/80" data-testid="sync-pull-full">
                {syncBusy && sync?.state?.status === "running" ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Syncing…</> : <><RefreshCw className="mr-2 h-4 w-4" /> Sync Now (Full)</>}
              </Button>
              <Button onClick={() => startSync(true)} disabled={syncBusy} variant="outline" className="rounded-none label-caps" data-testid="sync-pull-incremental">Incremental (changes only)</Button>
              {sync?.state?.status === "staged" && (
                <Button onClick={applySync} disabled={syncBusy} className="rounded-none label-caps bg-secondary text-secondary-foreground hover:bg-secondary/80" data-testid="sync-apply"><UploadCloud className="mr-2 h-4 w-4" /> Apply Staged to Live</Button>
              )}
              {!syncBusy && sync?.state?.status === "running" && (
                <Button onClick={() => startSync(false, true)} variant="outline" className="rounded-none label-caps border-destructive text-destructive hover:bg-destructive/10" data-testid="sync-force"><RefreshCw className="mr-2 h-4 w-4" /> Force Re-sync (unstick)</Button>
              )}
            </div>
            {!syncBusy && sync?.state?.status === "running" && (
              <p className="mt-2 text-xs text-muted-foreground" data-testid="sync-stuck-note">A previous sync shows as "running" — if it's been more than a minute, it likely stopped mid-way (e.g. a restart). Click <strong>Force Re-sync</strong> to start fresh.</p>
            )}

            {sync?.state?.status && (
              <p className="mt-3 label-caps text-muted-foreground" data-testid="sync-state">
                Status: <span className="text-primary">{sync.state.status}</span>
                {sync.state.generated_at ? ` · bundle ${new Date(sync.state.generated_at).toLocaleString()}` : ""}
                {sync.state.error ? ` · ${sync.state.error}` : ""}
              </p>
            )}

            <div className="mt-6 overflow-x-auto border border-border">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-border bg-background"><tr className="label-caps text-muted-foreground"><th className="p-3">Collection</th><th className="p-3">Live</th><th className="p-3">Staged</th></tr></thead>
                <tbody>
                  {sync && Object.keys(sync.live_counts).map((k) => (
                    <tr key={k} className="border-b border-border last:border-0"><td className="p-3 capitalize">{k.replace("_", " ")}</td><td className="p-3 font-mono">{sync.live_counts[k]}</td><td className="p-3 font-mono text-primary">{sync.staged_counts[k]}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>

            {preview && (
              <div className="mt-6" data-testid="sync-preview">
                <p className="label-caps text-muted-foreground">Staged Preview — {preview.subjects.length} subjects, sample courses:</p>
                <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {preview.sample_courses.map((c) => (
                    <div key={c.course_id} className="border border-border bg-background p-3 text-sm">
                      <span className="label-caps text-[0.55rem]" style={{ color: c.subject_name }}>{c.subject_name}</span>
                      <p className="font-semibold">{c.title}</p>
                      <p className="text-xs text-muted-foreground">Grade {c.grade_level}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Database Backup */}
          <div className="mt-6 border border-border bg-card p-6">
            <div className="flex items-center gap-2 text-primary"><UploadCloud className="h-5 w-5 rotate-180" /><span className="label-caps">Database Backup</span></div>
            <h3 className="mt-2 font-head text-2xl font-bold">Download a Full Database Snapshot</h3>
            <p className="mt-1 text-sm text-muted-foreground">Downloads a <strong>ZIP</strong> containing one JSON Lines file per collection (users, enrollments, courses, lessons, payments, Cover School, transactions, etc.) plus a <code>manifest.json</code> with counts. Restorable with <code>mongoimport</code>. Includes everything live — <strong>contains hashed passwords and payment history</strong>, so store securely.</p>
            <div className="mt-5">
              <Button onClick={downloadBackup} disabled={backupBusy} className="rounded-none label-caps bg-primary hover:bg-primary/80" data-testid="backup-download-btn">
                {backupBusy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Preparing backup…</> : <><UploadCloud className="mr-2 h-4 w-4 rotate-180" /> Download Database Backup (.zip)</>}
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* Users */}
        <TabsContent value="users" className="mt-6">
          {selUsers.size > 0 && (
            <div className="mb-3 flex items-center justify-between border border-destructive/50 bg-destructive/10 p-3" data-testid="users-bulk-bar">
              <span className="text-sm font-semibold">{selUsers.size} user(s) selected</span>
              <Button onClick={bulkDelUsers} className="rounded-none label-caps bg-destructive text-destructive-foreground hover:bg-destructive/80" data-testid="users-bulk-delete"><Trash2 className="mr-2 h-4 w-4" /> Delete Selected</Button>
            </div>
          )}
          <div className="overflow-x-auto border border-border">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-border bg-card"><tr className="label-caps text-muted-foreground">
                <th className="p-4 w-10"><input type="checkbox" data-testid="users-select-all" checked={users.length > 0 && selUsers.size === users.filter((u) => u.role !== "admin").length && selUsers.size > 0} onChange={(e) => setSelUsers(e.target.checked ? new Set(users.filter((u) => u.role !== "admin").map((u) => u.id)) : new Set())} /></th>
                <th className="p-4">Name</th><th className="p-4">Email</th><th className="p-4">Role</th><th className="p-4">Joined</th><th className="p-4">Actions</th></tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-border last:border-0" data-testid={`user-row-${u.id}`}>
                    <td className="p-4">{u.role !== "admin" && <input type="checkbox" data-testid={`user-select-${u.id}`} checked={selUsers.has(u.id)} onChange={() => toggleSet(selUsers, setSelUsers, u.id)} />}</td>
                    <td className="p-4">{u.name}</td><td className="p-4">{u.email}</td><td className="p-4"><span className={`label-caps ${u.role === "admin" ? "text-primary" : "text-muted-foreground"}`}>{u.role}</span></td><td className="p-4">{fmtDate(u.created_at)}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <button onClick={() => setEnrollDlg({ user_id: u.id, name: u.name, grade_level: "K", student_name: u.name })} className="inline-flex items-center gap-1 text-xs font-bold text-primary hover:underline" data-testid={`user-enroll-${u.id}`}><GraduationCap className="h-4 w-4" /> Enroll</button>
                        {u.role !== "admin" && <button onClick={() => delUser(u.id)} className="text-muted-foreground hover:text-destructive" data-testid={`user-delete-${u.id}`}><Trash2 className="h-4 w-4" /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">Delete permanently removes the account and all its enrollments, memberships, payments, and progress. Admin accounts can't be deleted.</p>
        </TabsContent>

        {/* Enrollments */}
        <TabsContent value="enrollments" className="mt-6">
          {selEnr.size > 0 && (
            <div className="mb-3 flex items-center justify-between border border-destructive/50 bg-destructive/10 p-3" data-testid="enr-bulk-bar">
              <span className="text-sm font-semibold">{selEnr.size} enrollment(s) selected</span>
              <Button onClick={bulkDelEnr} className="rounded-none label-caps bg-destructive text-destructive-foreground hover:bg-destructive/80" data-testid="enr-bulk-delete"><Trash2 className="mr-2 h-4 w-4" /> Delete Selected</Button>
            </div>
          )}
          <div className="overflow-x-auto border border-border">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-border bg-card"><tr className="label-caps text-muted-foreground">
                <th className="p-4 w-10"><input type="checkbox" data-testid="enr-select-all" checked={enrollments.length > 0 && selEnr.size === enrollments.length} onChange={(e) => setSelEnr(e.target.checked ? new Set(enrollments.map((x) => x.enrollment_id)) : new Set())} /></th>
                <th className="p-4">Student</th><th className="p-4">Grade</th><th className="p-4">Status</th><th className="p-4">Expires</th><th className="p-4">Actions</th></tr></thead>
              <tbody>
                {enrollments.map((e) => (
                  <tr key={e.enrollment_id} className="border-b border-border last:border-0" data-testid={`enr-row-${e.enrollment_id}`}>
                    <td className="p-4"><input type="checkbox" data-testid={`enr-select-${e.enrollment_id}`} checked={selEnr.has(e.enrollment_id)} onChange={() => toggleSet(selEnr, setSelEnr, e.enrollment_id)} /></td>
                    <td className="p-4">{e.student_name}</td><td className="p-4">{e.grade_level}</td><td className="p-4"><span className="label-caps text-primary">{e.status}</span></td><td className="p-4">{fmtDate(e.expires_at)}</td>
                    <td className="p-4"><button onClick={() => delEnrollment(e.enrollment_id)} className="text-muted-foreground hover:text-destructive" data-testid={`enr-delete-${e.enrollment_id}`}><Trash2 className="h-4 w-4" /></button></td>
                  </tr>
                ))}
                {enrollments.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No enrollments yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Premium Courses */}
        <TabsContent value="premium" className="mt-6">
          <div className="flex items-center gap-2 border-b border-border pb-5 text-primary"><DollarSign className="h-5 w-5" /><span className="label-caps">Premium Access</span></div>

          {/* Subject Bundles (College Prep programs) */}
          <div className="mt-6 border border-border bg-card p-5">
            <div className="flex items-center gap-2 text-primary"><GraduationCap className="h-5 w-5" /><span className="label-caps font-bold">College Prep Program Bundles</span></div>
            <p className="mt-2 text-sm text-muted-foreground">Flip any College Prep subject (e.g. <strong>Criminal Justice</strong>) into a <strong>program bundle</strong> and set <strong>one price</strong>. Students who buy it get <strong>every course under that subject</strong> — current and future. When a bundle is on, the per-course premium prices below are ignored for that subject.</p>
            <div className="mt-4 overflow-x-auto border border-border">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-border bg-background"><tr className="label-caps text-muted-foreground">
                  <th className="p-4">Subject</th><th className="p-4">Courses</th><th className="p-4">Bundle</th><th className="p-4">Program Price</th><th className="p-4">Unlocks</th></tr></thead>
                <tbody>
                  {premiumSubjects.map((s) => (
                    <tr key={s.subject_id} className="border-b border-border last:border-0" data-testid={`bundle-row-${s.subject_id}`}>
                      <td className="p-4 font-semibold">{s.name}</td>
                      <td className="p-4 text-muted-foreground">{s.course_count}</td>
                      <td className="p-4"><Switch checked={s.is_premium_bundle} onCheckedChange={() => toggleBundle(s)} data-testid={`bundle-toggle-${s.subject_id}`} /></td>
                      <td className="p-4">
                        {s.is_premium_bundle ? (
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground">$</span>
                            <Input type="number" min="0" step="0.01" value={bundleEdits[s.subject_id] ?? (s.bundle_price || "")} placeholder="Set price…" onChange={(e) => setBundleEdits((p) => ({ ...p, [s.subject_id]: e.target.value }))} className="h-9 w-28 rounded-none" data-testid={`bundle-price-${s.subject_id}`} />
                            <Button size="sm" onClick={() => saveBundlePrice(s)} className="h-9 rounded-none label-caps" data-testid={`bundle-save-${s.subject_id}`}><Save className="h-4 w-4" /></Button>
                          </div>
                        ) : <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="p-4">{s.is_premium_bundle ? s.unlocks : "—"}</td>
                    </tr>
                  ))}
                  {premiumSubjects.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">No College Prep subjects yet. Mark subjects as “College Prep” in the Curriculum tab first.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-10 flex items-center gap-2 border-b border-border pb-3 text-muted-foreground"><DollarSign className="h-4 w-4" /><span className="label-caps text-xs">Per-Course Premium Electives</span></div>
          <p className="mt-3 text-sm text-muted-foreground">Flip any course to <strong>Premium</strong> and set its one-time unlock price. (Courses inside an active program bundle above are sold at the bundle price instead.)</p>
          <Input value={premiumSearch} onChange={(e) => setPremiumSearch(e.target.value)} placeholder="Search courses…" className="mt-4 max-w-md rounded-none" data-testid="premium-search" />
          <div className="mt-4 overflow-x-auto border border-border">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-border bg-card"><tr className="label-caps text-muted-foreground">
                <th className="p-4">Course</th><th className="p-4">Grade</th><th className="p-4">Subject</th><th className="p-4">Premium</th><th className="p-4">Price (USD)</th><th className="p-4">Unlocks</th></tr></thead>
              <tbody>
                {premiumCourses.filter((c) => !premiumSearch || (c.title || "").toLowerCase().includes(premiumSearch.toLowerCase())).slice(0, 200).map((c) => (
                  <tr key={c.course_id} className="border-b border-border last:border-0" data-testid={`premium-row-${c.course_id}`}>
                    <td className="p-4 font-semibold">{c.title}</td>
                    <td className="p-4">{c.grade_level === "K" ? "K" : `Gr ${c.grade_level}`}</td>
                    <td className="p-4">{c.subject_name}</td>
                    <td className="p-4"><Switch checked={c.is_premium} onCheckedChange={() => togglePremium(c)} data-testid={`premium-toggle-${c.course_id}`} /></td>
                    <td className="p-4">
                      {c.is_premium ? (
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">$</span>
                          <Input type="number" min="0" step="0.01" value={priceEdits[c.course_id] ?? c.premium_price} onChange={(e) => setPriceEdits((p) => ({ ...p, [c.course_id]: e.target.value }))} className="h-9 w-24 rounded-none" data-testid={`premium-price-${c.course_id}`} />
                          <Button size="sm" onClick={() => savePrice(c)} className="h-9 rounded-none label-caps" data-testid={`premium-save-${c.course_id}`}><Save className="h-4 w-4" /></Button>
                        </div>
                      ) : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="p-4">{c.is_premium ? c.unlocks : "—"}</td>
                  </tr>
                ))}
                {premiumCourses.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No courses found.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Gradebook */}
        <TabsContent value="gradebook" className="mt-6">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-5">
            <div className="flex items-center gap-2 text-primary"><ClipboardList className="h-5 w-5" /><span className="label-caps">Student Gradebook</span></div>
            <Button onClick={loadProgress} variant="outline" size="sm" className="rounded-none label-caps" data-testid="gradebook-refresh"><RefreshCw className="mr-2 h-4 w-4" /> Refresh</Button>
          </div>
          <p className="mt-3 text-sm text-muted-foreground">{progress.length} completed quiz & assignment attempt{progress.length === 1 ? "" : "s"} across all students. (Admin preview-mode attempts are not recorded.)</p>
          <div className="mt-5 overflow-x-auto border border-border">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-border bg-card"><tr className="label-caps text-muted-foreground">
                <th className="p-4">Student</th><th className="p-4">Subject</th><th className="p-4">Grade</th><th className="p-4">Lesson</th><th className="p-4">Type</th><th className="p-4">Score</th><th className="p-4">Result</th><th className="p-4">Date & Time</th></tr></thead>
              <tbody>
                {progress.map((r, i) => (
                  <tr key={i} className="border-b border-border last:border-0" data-testid={`gradebook-row-${i}`}>
                    <td className="p-4"><span className="font-semibold">{r.student_name || "—"}</span><br /><span className="text-xs text-muted-foreground">{r.email}</span></td>
                    <td className="p-4">{r.subject || "—"}</td>
                    <td className="p-4">{r.grade_level ? GRADE_LABEL(String(r.grade_level)) : "—"}</td>
                    <td className="p-4">{r.lesson_title || "—"}</td>
                    <td className="p-4"><span className="label-caps text-muted-foreground">{r.kind}</span></td>
                    <td className="p-4 font-bold">{r.score_pct}% <span className="text-xs font-normal text-muted-foreground">({r.points_earned}/{r.total_points})</span></td>
                    <td className="p-4"><span className={`label-caps ${r.passed ? "text-primary" : "text-destructive"}`}>{r.passed ? "Passed" : "Failed"}</span></td>
                    <td className="p-4 whitespace-nowrap">{fmtDateTime(r.date)}</td>
                  </tr>
                ))}
                {progress.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">No completed quizzes or assignments yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* Cover School */}
        <TabsContent value="cover-school" className="mt-6">
          <div className="flex items-center gap-2 border-b border-border pb-5 text-primary"><ShieldCheck className="h-5 w-5" /><span className="label-caps">Alabama Cover School</span></div>
          <p className="mt-3 text-sm text-muted-foreground">{coverEnr.length} enrolled famil{coverEnr.length === 1 ? "y" : "ies"}. Click a document to open a printable copy.</p>
          <div className="mt-5 space-y-4">
            {coverEnr.map((c) => (
              <div key={c.enrollment_id} className="border border-border bg-card p-5" data-testid={`cover-row-${c.enrollment_id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="font-head text-lg font-bold">{[c.parent1_first, c.parent1_last].filter(Boolean).join(" ")}{c.parent2_first ? ` & ${[c.parent2_first, c.parent2_last].filter(Boolean).join(" ")}` : ""}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{c.email} · {c.phone}</p>
                    <p className="text-sm text-muted-foreground">{c.address}, {c.city}, {c.state} {c.zip}{c.county ? ` · ${c.county} County` : ""}</p>
                    <p className="mt-2 text-sm"><span className="label-caps text-muted-foreground">School Year</span> {c.school_year} · <span className="label-caps text-muted-foreground">Students</span> {(c.students || []).map((s) => s.name).join(", ")}</p>
                  </div>
                  <span className="label-caps text-primary">{c.status}</span>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {[["welcome", "Welcome Letter"], ["enrollment_form", "Enrollment Form"], ["boe_instructions", "BOE Instructions"], ["records_request", "Records Request"]].map(([t, lbl]) => (
                    <Button key={t} onClick={() => openCoverDoc(c.enrollment_id, t)} variant="outline" size="sm" className="rounded-none label-caps" data-testid={`cover-doc-${t}-${c.enrollment_id}`}><FileText className="mr-2 h-4 w-4" /> {lbl}</Button>
                  ))}
                </div>
              </div>
            ))}
            {coverEnr.length === 0 && <div className="border border-border bg-card p-10 text-center text-muted-foreground">No cover school enrollments yet.</div>}
          </div>
        </TabsContent>

        {/* Transactions */}
        <TabsContent value="txns" className="mt-6">
          <div className="overflow-x-auto border border-border">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-border bg-card"><tr className="label-caps text-muted-foreground"><th className="p-4">Email</th><th className="p-4">Type</th><th className="p-4">Amount</th><th className="p-4">Status</th><th className="p-4">Date</th></tr></thead>
              <tbody>
                {txns.map((t, i) => (
                  <tr key={i} className="border-b border-border last:border-0"><td className="p-4">{t.email}</td><td className="p-4 capitalize">{(t.type || "").replace("_", " ")}</td><td className="p-4">${t.amount?.toFixed(2)}</td><td className="p-4"><span className={`label-caps ${t.payment_status === "paid" ? "text-primary" : "text-muted-foreground"}`}>{t.payment_status}</span></td><td className="p-4">{fmtDate(t.created_at)}</td></tr>
                ))}
                {txns.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">No transactions yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Grant access dialog */}
      <Dialog open={!!enrollDlg} onOpenChange={(o) => !o && setEnrollDlg(null)}>
        <DialogContent data-testid="enroll-dialog">
          <DialogHeader><DialogTitle>Grant access to {enrollDlg?.name}</DialogTitle></DialogHeader>
          <div className="space-y-6">
            {/* Base grade enrollment */}
            <div className="space-y-3">
              <p className="label-caps text-primary">Grade Enrollment</p>
              <p className="text-xs text-muted-foreground">Free 1-year active enrollment (no payment) — unlocks that grade's regular courses.</p>
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <Label>Grade</Label>
                  <Select value={enrollDlg?.grade_level} onValueChange={(v) => setEnrollDlg((d) => ({ ...d, grade_level: v }))}>
                    <SelectTrigger className="mt-1 rounded-none" data-testid="enroll-grade-trigger"><SelectValue /></SelectTrigger>
                    <SelectContent>{GRADES.map((g) => <SelectItem key={g} value={g} data-testid={`enroll-grade-${g}`}>{GRADE_LABEL(g)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Button onClick={grantEnroll} disabled={enrollBusy} className="rounded-none label-caps" data-testid="enroll-confirm">{enrollBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enroll"}</Button>
              </div>
              <div>
                <Label>Student name</Label>
                <Input value={enrollDlg?.student_name || ""} onChange={(e) => setEnrollDlg((d) => ({ ...d, student_name: e.target.value }))} className="mt-1 rounded-none" data-testid="enroll-student-name" />
              </div>
            </div>

            {/* Premium course unlock */}
            <div className="space-y-3 border-t border-border pt-5">
              <p className="label-caps text-primary">Unlock a Premium Elective</p>
              <p className="text-xs text-muted-foreground">Comps a permanent unlock for one premium course (no payment) — so this account can go through it as a real student.</p>
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <Label>Premium course</Label>
                  <Select value={enrollDlg?.unlock_course_id || ""} onValueChange={(v) => setEnrollDlg((d) => ({ ...d, unlock_course_id: v }))}>
                    <SelectTrigger className="mt-1 rounded-none" data-testid="unlock-course-trigger"><SelectValue placeholder="Choose a premium course…" /></SelectTrigger>
                    <SelectContent>
                      {premiumCourses.filter((c) => c.is_premium).map((c) => (
                        <SelectItem key={c.course_id} value={c.course_id} data-testid={`unlock-course-${c.course_id}`}>{c.title} · {c.grade_level === "K" ? "K" : `Gr ${c.grade_level}`}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={grantUnlock} disabled={enrollBusy} className="rounded-none label-caps" data-testid="unlock-confirm">{enrollBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Unlock"}</Button>
              </div>
              {premiumCourses.filter((c) => c.is_premium).length === 0 && <p className="text-xs text-muted-foreground">No premium courses yet — mark some in the Premium tab first.</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEnrollDlg(null)} className="rounded-none label-caps" data-testid="enroll-close">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
